import { isUndefined } from 'lodash-es';
import { computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { GeolocationError } from '../models';
import { useGeolocationStore } from '../stores/geolocationStore';
import { storeToRefs } from 'pinia';

function _getGeolocation(abortController?: AbortController): Promise<GeolocationPosition | null> {
    return new Promise((resolve, reject) => {
        if (abortController) {
            abortController.signal.addEventListener('abort', () => resolve(null));
        }

        navigator.geolocation.getCurrentPosition(
            position => {
                if (abortController?.signal.aborted) return;

                resolve(position);
            },
            reject,
            { enableHighAccuracy: true, timeout: 30000 }
        );
    });
}

export function useGeolocationHelper() {
    const { isAllowed } = storeToRefs(useGeolocationStore());
    const { t } = useI18n();
    const isSupported = computed(() => !isUndefined(navigator.geolocation));
    let permissionStatus: PermissionStatus | null = null;

    function updatePermissionState() {
        isAllowed.value = permissionStatus?.state === 'granted';
    }

    onMounted(async () => {
        permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

        // Add event listener for permission changes
        permissionStatus.addEventListener('change', updatePermissionState);

        updatePermissionState();
    });

    onUnmounted(() => {
        // Clean up event listener when component unmounts
        if (permissionStatus) {
            permissionStatus.removeEventListener('change', updatePermissionState);
        }
    });

    async function getCurrentPosition(abortController?: AbortController) {
        // abort any existing requests
        abortController?.abort();

        try {
            // reset abortController
            abortController = new AbortController();
            // request for geolocation
            const position = await _getGeolocation(abortController);

            // if we got here, we have permission
            // this is a workaround for permission state not being updated
            // via event listener on browsers like Safari and Firefox
            isAllowed.value = true;

            // set if geolocation is valid
            if (!position)
                throw new GeolocationError(
                    t('places.resources.errorMessages.geolocationGeneralError', 'Geolocation failed. Please try again.')
                );

            return position;
        } catch (e) {
            if (e instanceof GeolocationPositionError) {
                switch (e.code) {
                    case 1:
                        throw new GeolocationError(
                            t(
                                'places.resources.errorMessages.geolocationPermissionError',
                                'Please allow the browser to access your location.'
                            )
                        );

                    default:
                        return null;
                }
            }

            throw e;
        }
    }

    return {
        isSupported,
        isAllowed,
        getCurrentPosition
    };
}
